import type { JSX } from "react";
import MuiAvatar from "@mui/material/Avatar";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { hasName, hasSafeAvatar, initials } from "../avatar";

/**
 * Avatar — the app's profile mark, with the SAME 3-tier precedence the Me-slice
 * ProfileSummary uses (shared helpers in `common/avatar.ts`):
 *
 *   1. an https:// avatar URL → <img> (alt = name, or "avatar"); no initials/svg
 *   2. else a usable name     → INITIALS ("Pat Player" → "PP"); no <img>
 *   3. else (blank/no name)   → the generic `person` <Icon> svg (never an empty
 *                               box)
 *
 * It NEVER renders an empty placeholder circle and NEVER trusts a non-https URL
 * into an <img src> (a poisoned row could carry javascript:/data:/http:). The
 * non-image fallback is aria-labelled by the name when one is known.
 */
export function Avatar({
  name,
  avatarUrl,
  size = 28,
  className,
}: {
  name?: string | null;
  avatarUrl?: string | null;
  size?: number;
  className?: string;
}): JSX.Element {
  const sharedProps = {
    className,
    sx: {
      width: size,
      height: size,
      bgcolor: "action.selected",
      color: "semantic.onSelected",
      border: "1px solid",
      borderColor: "divider",
      fontSize: Math.max(11, Math.round(size * 0.34)),
      fontWeight: 800,
    },
  } as const;

  // Tier 1: a safe https image. We render ONLY the image (no initials/person).
  if (hasSafeAvatar(avatarUrl)) {
    return (
      <MuiAvatar {...sharedProps} src={avatarUrl} alt={name || "Player avatar"} />
    );
  }

  // Tier 2: a usable name → initials, accessible-named by the person's name.
  if (hasName(name)) {
    return (
      <MuiAvatar {...sharedProps} role="img" aria-label={name}>
        <span aria-hidden="true">{initials(name)}</span>
      </MuiAvatar>
    );
  }

  // Tier 3: no name, no safe url → the generic person icon (never an empty box).
  return (
    <MuiAvatar {...sharedProps}>
      <PersonRoundedIcon
        aria-hidden="true"
        sx={{ fontSize: Math.round(size * 0.64) }}
      />
    </MuiAvatar>
  );
}
